import { FILE_EXTENSION, FILE_MIME } from '../config.ts';
import { asBufferSource } from './bytes.ts';

export type WriteSink = {
  write: (chunk: Uint8Array) => Promise<void>;
  close: () => Promise<void>;
  abort: () => Promise<void>;
  /** When using blob fallback, returns the finished blob after close. */
  resultBlob?: () => Blob | null;
};

type SavePickerWindow = Window &
  typeof globalThis & {
    showSaveFilePicker?: (options?: {
      suggestedName?: string;
      types?: Array<{ description: string; accept: Record<string, string[]> }>;
    }) => Promise<{
      createWritable: () => Promise<{
        write: (data: BufferSource | Blob) => Promise<void>;
        close: () => Promise<void>;
        abort: () => Promise<void>;
      }>;
    }>;
  };

/** Prefer File System Access API, else accumulate ciphertext Blob parts. */
export async function createWriteSink(suggestedName: string): Promise<{
  sink: WriteSink;
  mode: 'fs-access' | 'blob';
}> {
  const w = window as SavePickerWindow;
  if (typeof w.showSaveFilePicker === 'function') {
    try {
      const handle = await w.showSaveFilePicker({
        suggestedName,
        types: [
          {
            description: 'ZeroCrypt encrypted file',
            accept: { [FILE_MIME]: [FILE_EXTENSION] },
          },
        ],
      });
      const writable = await handle.createWritable();
      return {
        mode: 'fs-access',
        sink: {
          write: async (chunk) => {
            await writable.write(asBufferSource(chunk));
          },
          close: async () => {
            await writable.close();
          },
          abort: async () => {
            await writable.abort();
          },
        },
      };
    } catch (err) {
      // User cancel should propagate; other failures fall back to blob.
      if (err instanceof DOMException && err.name === 'AbortError') throw err;
    }
  }

  const parts: BlobPart[] = [];
  let finished: Blob | null = null;
  return {
    mode: 'blob',
    sink: {
      write: async (chunk) => {
        const copy = new Uint8Array(chunk.byteLength);
        copy.set(chunk);
        parts.push(copy);
      },
      close: async () => {
        finished = new Blob(parts, { type: FILE_MIME });
      },
      abort: async () => {
        parts.length = 0;
        finished = null;
      },
      resultBlob: () => finished,
    },
  };
}

export async function writeAllToSink(sink: WriteSink, data: Uint8Array): Promise<void> {
  await sink.write(data);
}
